import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {EditorViewport} from '@FormMakerReact/form-maker.types';
import {useUndoRedoState} from '@FormMakerReact/hooks/use-undo-redo-state';
import {FORM_MAKER_CLOSE_EMBEDDED_COMMAND, FORM_MAKER_SAVE_AND_CLOSE_EMBEDDED_COMMAND} from '@FormMakerReact/grapesjs-form-maker-plugin/src/form-maker-commands';
import {getAssetUrl} from '@Utils/s3.util';
import {HelpButton} from '../help-button/help-button';
import {SaveBadge} from '../save-badge/save-badge';
import {ShareButton} from '../share-button/share-button';
import {UndoRedoButtons} from '../undo-redo-buttons/undo-redo-buttons';
import styles from './form-maker-embedded-nav.module.scss';

function resolveLogoSrc(editorViewport: EditorViewport): string {
  if (editorViewport === EditorViewport.DESKTOP) {
    return getAssetUrl('logo-postermaker.svg');
  }
  return getAssetUrl('logo-mobile.svg');
}

export function FormMakerEmbeddedNav(): ReactElement {
  const {editor, state, actions, store} = useContext(formMakerContext);
  const {canUndo, canRedo} = useUndoRedoState(editor);
  const isMobileScreen = state.editorViewport === EditorViewport.MOBILE;

  function handleBackClick(): void {
    editor?.Commands.run(FORM_MAKER_CLOSE_EMBEDDED_COMMAND, {isSilentClose: false});
  }

  function handleDoneClick(): void {
    if (state.isEditorLoading || state.isSaving) {
      return;
    }

    editor?.Commands.run(FORM_MAKER_SAVE_AND_CLOSE_EMBEDDED_COMMAND);
  }

  function renderBackButton(): ReactElement | null {
    return (
      <Icon
        ariaLabel={window.i18next.t('pmwjs_back')}
        className={styles.backButton}
        icon="icon-arrow-left"
        isUsedAsButton
        onClick={handleBackClick}
        shape={IconShape.SQUARE}
        size={IconSize.SIZE_ICON_20}
        type={IconType.DARK_GHOST}
      />
    );
  }

  function renderHelpAndShareButtons(): ReactElement | null {
    if (isMobileScreen) {
      return null;
    }
    return (
      <>
        <HelpButton />
        <ShareButton />
      </>
    );
  }

  function renderDoneButton(): ReactElement {
    return (
      <Button
        icon="icon-arrow-right"
        iconPlacement={IconPlacement.RIGHT}
        isLoading={state.isEditorLoading}
        onClick={handleDoneClick}
        size={ButtonSize.MEDIUM}
        text={store.nextBtnCaption || window.i18next.t('pmwjs_done')}
        type={ButtonType.SECONDARY}
      />
    );
  }

  return (
    <header className={styles.navWrapper}>
      <nav className={styles.navbar} aria-label={window.i18next.t('pmwjs_form_maker_header_aria')}>
        <div className={styles.left}>
          {renderBackButton()}
          <img className={styles.logo} src={resolveLogoSrc(state.editorViewport)} alt="PosterMyWall" />
          <div className={styles.undoRedoSlot}>
            <UndoRedoButtons actions={actions} canUndo={canUndo} canRedo={canRedo} />
          </div>
        </div>

        <div className={styles.right}>
          <SaveBadge state={state} onRetry={actions.save} />
          <div className={styles.divider} />
          {renderHelpAndShareButtons()}
          {renderDoneButton()}
        </div>
      </nav>
    </header>
  );
}
